import type { CaptureResult } from 'posthog-js'
import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'

import type { User } from '@scrambl/shared'

type AnalyticsUser = Pick<User, 'id' | 'email' | 'role' | 'createdAt'> & {
  displayName?: string | null
}

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ??
  'https://us.i.posthog.com'
const APP_URL =
  (import.meta.env.VITE_APP_URL as string | undefined) ??
  'http://localhost:3005'
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined
const APP_ENV =
  (import.meta.env.VITE_APP_ENV as string | undefined) ??
  (import.meta.env.PROD ? 'production' : 'development')
const APP_VERSION = import.meta.env.VITE_APP_VERSION as string | undefined
const POSTHOG_CAPTURE =
  APP_ENV === 'production' ||
  (
    import.meta.env.VITE_POSTHOG_CAPTURE as string | undefined
  )?.toLowerCase() === 'true'
const IS_TAURI = typeof window !== 'undefined' && '__TAURI__' in window
const APP_PLATFORM = IS_TAURI ? 'desktop' : 'web'
const APP_RUNTIME = IS_TAURI ? 'tauri' : 'browser'

let posthogReady = false
let sentryReady = false

// Event catalog — the canonical list of custom events the app emits.
// PostHog autocapture handles pageviews, clicks, and form submits; these
// cover product moments autocapture can't infer cleanly.
export type EventName =
  | 'feature_used'
  | 'module_opened'
  | 'task_created'
  | 'task_completed'
  | 'project_created'
  | 'benny_message_sent'
  | 'export_generated'
  | 'invite_sent'
  | 'help_article_viewed'
  | 'help_search_performed'
  | 'help_feedback_submitted'
  | 'scenario_created'
  | 'scenario_calculated'
  | 'scenario_comparison_opened'
  | 'scenario_results_viewed'

export interface EventProperties {
  // Free-form; keep keys stable and snake_case.
  [key: string]: string | number | boolean | null | undefined
}

function getAnalyticsUrl(path?: string): string {
  const fallbackPath =
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : '/'

  try {
    return new URL(path ?? fallbackPath, APP_URL).toString()
  } catch {
    return path ?? fallbackPath
  }
}

function getAnalyticsSuperProperties(): EventProperties {
  return {
    app_platform: APP_PLATFORM,
    app_runtime: APP_RUNTIME,
    app_environment: APP_ENV,
    app_version: APP_VERSION,
  }
}

function normalizeDesktopEvent(
  event: CaptureResult | null,
): CaptureResult | null {
  if (!event || !IS_TAURI) return event

  const path =
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : undefined
  const analyticsUrl = getAnalyticsUrl(path)

  let host: string | undefined
  try {
    host = new URL(analyticsUrl).host
  } catch {
    host = undefined
  }

  return {
    ...event,
    properties: {
      ...event.properties,
      ...getAnalyticsSuperProperties(),
      $current_url: analyticsUrl,
      $host: host,
      $pathname:
        typeof window !== 'undefined' ? window.location.pathname : undefined,
    },
  }
}

export function initAnalytics(): void {
  if (POSTHOG_KEY && !posthogReady) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      api_transport: 'fetch',
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      persistence: IS_TAURI ? 'localStorage' : 'localStorage+cookie',
      cross_subdomain_cookie: !IS_TAURI,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '.ph-mask',
      },
      disable_session_recording: false,
      before_send: normalizeDesktopEvent,
      loaded: ph => {
        if (!POSTHOG_CAPTURE) {
          ph.opt_out_capturing()
        } else {
          ph.opt_in_capturing()
        }
      },
    })
    posthog.register(getAnalyticsSuperProperties())
    posthogReady = true
  }

  if (SENTRY_DSN && !sentryReady) {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: APP_ENV,
      release: APP_VERSION,
      tracesSampleRate: APP_ENV === 'production' ? 0.1 : 1.0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      integrations: [Sentry.browserTracingIntegration()],
    })
    sentryReady = true
  }
}

export function identify(user: AnalyticsUser, orgId: string | null): void {
  if (!user?.id) return

  if (posthogReady) {
    posthog.identify(user.id, {
      email: user.email,
      name: user.displayName,
      role: user.role,
      created_at: user.createdAt,
    })
    if (orgId) {
      posthog.group('organization', orgId)
    }
  }

  if (sentryReady) {
    Sentry.setUser({ id: user.id, email: user.email })
    if (orgId) {
      Sentry.setTag('organization_id', orgId)
    }
  }
}

export function resetAnalytics(): void {
  if (posthogReady) {
    posthog.reset()
  }
  if (sentryReady) {
    Sentry.setUser(null)
  }
}

export function track(event: EventName, properties?: EventProperties): void {
  if (posthogReady) {
    posthog.capture(event, {
      ...getAnalyticsSuperProperties(),
      ...properties,
    })
  }
  void sendServerEvent(event, properties)
}

async function sendServerEvent(
  event: string,
  properties?: EventProperties,
): Promise<void> {
  const apiUrl =
    (import.meta.env.VITE_API_URL as string | undefined) ??
    'http://localhost:4005'
  try {
    await fetch(`${apiUrl}/user-events/track`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eventType: event, payload: properties ?? {} }),
      keepalive: true,
    })
  } catch {
    // best-effort — never block user flow on telemetry
  }
}

export function trackPageview(path: string): void {
  if (!posthogReady) return
  posthog.capture('$pageview', {
    ...getAnalyticsSuperProperties(),
    $current_url: getAnalyticsUrl(path),
  })
}
